/**
 * The three small pickers Tasks opens from a chip or a field — quick due
 * dates (§8.5, Board 19 A: 318 wide), a person (§8.2's *+ Add a person*)
 * and a record to attach (§8.2's *Add*). Each is a `Popover` on a desktop
 * and a `PanelSheet` with 52 px rows on a phone (§10.2: no native select).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { zonedLocal, type ActivityPerson } from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { BackLayer } from '../use-activity-history.js'
import { DateField } from '../../components/date-field.js'
import { Button } from '../../components/primitives.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { Glyph } from '../glyphs.js'
import { Face } from '../faces.js'
import { linkGlyph } from './parts.js'
import type { TaskListMeets } from './types.js'
import { dayLabel, quickDays } from './words.js'

/** A popover on a desktop, a bottom sheet on a phone — the body is the same either way. */
function Floating({
  open, onClose, anchor, width, label, children, align = 'left',
}: {
  open: boolean; onClose: () => void; anchor: HTMLElement | null; width: number; label: string
  children: ReactNode; align?: 'left' | 'right'
}) {
  const phone = usePhone()
  if (!open) return null
  /* Back closes the picker first (Aaron, Oct 26). */
  if (phone) return <><BackLayer onBack={onClose} /><PanelSheet open onClose={onClose} title={label}>{children}</PanelSheet></>
  return (
    <Popover open onClose={onClose} anchor={anchor} width={width} align={align} label={label}
      className="rounded-xl" layer={70}>
      {children}
    </Popover>
  )
}

export interface DuePick { date: string | null; minutes?: number | null }

/**
 * §8.5's quick due dates: *Today · Tomorrow · Next <meeting> · Next week ·
 * No date · Pick a date…*, each with its day faint at the right; the row the
 * task is already on is tinted.
 */
export function QuickDueMenu({
  open, onClose, anchor, today, current, meets, timeZone, onPick,
}: {
  open: boolean
  onClose: () => void
  anchor: HTMLElement | null
  today: string
  /** The task's due day now, or null. */
  current: string | null
  /** The meetings of the lists it is in. */
  meets: readonly TaskListMeets[]
  timeZone: string
  onPick: (pick: DuePick) => void
}) {
  const phone = usePhone()
  const [picking, setPicking] = useState(false)
  const [picked, setPicked] = useState('')
  useEffect(() => { if (!open) { setPicking(false); setPicked('') } }, [open])
  const days = quickDays(today)
  const rows: { key: string; label: string; right: string; pick: DuePick; day: string | null }[] = [
    { key: 'today', label: 'Today', right: dayLabel(days.today), pick: { date: days.today }, day: days.today },
    { key: 'tomorrow', label: 'Tomorrow', right: dayLabel(days.tomorrow), pick: { date: days.tomorrow }, day: days.tomorrow },
    ...meets.filter((m) => m.nextAt).map((m) => {
      const local = zonedLocal(new Date(m.nextAt!), timeZone)
      return {
        key: `meet:${m.meetingId}`, label: m.quickLabel, right: m.nextAtWords ?? dayLabel(local.date),
        pick: { date: local.date, minutes: local.minutes }, day: local.date,
      }
    }),
    { key: 'next-week', label: 'Next week', right: dayLabel(days.nextWeek), pick: { date: days.nextWeek }, day: days.nextWeek },
    { key: 'none', label: 'No date', right: '', pick: { date: null }, day: null },
  ]
  /* The row it is on now: a meeting's when it is the meeting's day, else the plain day. */
  const chosen = rows.find((row) => row.key.startsWith('meet:') && row.day === current)?.key
    ?? rows.find((row) => row.day === current)?.key ?? null

  const choose = (pick: DuePick) => { onPick(pick); onClose() }

  return (
    <Floating open={open} onClose={onClose} anchor={anchor} width={318} label="Due">
      <div className={cn('pb-1.5', phone ? 'pt-1' : 'pt-1')}>
        {!phone && <div className="px-3 pb-1 pt-2 text-[11px] font-semibold text-text-faint">Due</div>}
        {rows.map((row) => (
          <button key={row.key} type="button" onClick={() => choose(row.pick)}
            className={cn('mx-1 flex w-[calc(100%-8px)] items-center gap-2.5 whitespace-nowrap rounded-lg px-3 text-left text-[13px]',
              phone ? 'h-[52px]' : 'h-[34px]',
              row.key === chosen ? 'bg-green-band-bg' : 'hover:bg-hover')}>
            <span className={row.key === chosen ? 'font-semibold text-accent-dark' : 'font-medium text-text'}>{row.label}</span>
            <span className="flex-1" />
            <span className="text-[11.5px] text-text-muted">{row.right}</span>
          </button>
        ))}
        <div className="mt-1 border-t border-line-faint px-3 pb-1 pt-2">
          {picking ? (
            <span className="flex items-center gap-2">
              <DateField value={picked} onChange={setPicked} label="Due" size="small" display="day" className="flex-1" />
              <Button variant="primary" disabled={!picked} onClick={() => choose({ date: picked })}>Set</Button>
            </span>
          ) : (
            <button type="button" onClick={() => setPicking(true)}
              className={cn('flex items-center gap-1.5 text-[12.5px] font-semibold text-accent', phone && 'h-11')}>
              <Glyph name="calendar" size={13} />Pick a date…
            </button>
          )}
        </div>
      </div>
    </Floating>
  )
}

/** *+ Add a person* (§8.2, §8.6): the people this person may put on a task, searched by name. */
export function PersonPicker({
  open, onClose, anchor, people, taken, onPick,
}: {
  open: boolean
  onClose: () => void
  anchor: HTMLElement | null
  people: readonly ActivityPerson[]
  taken: readonly string[]
  onPick: (person: ActivityPerson) => void
}) {
  const phone = usePhone()
  const [q, setQ] = useState('')
  useEffect(() => { if (!open) setQ('') }, [open])
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase()
    return people.filter((p) => !taken.includes(p.id) && (!term || p.name.toLowerCase().includes(term)))
  }, [people, taken, q])
  return (
    <Floating open={open} onClose={onClose} anchor={anchor} width={300} label="Add a person">
      <div className="p-2">
        <label className="flex h-9 items-center gap-2 rounded-lg border border-line px-2.5 text-[13px] text-text-faint">
          <Glyph name="search" size={14} />
          <input autoFocus value={q} onChange={(event) => setQ(event.target.value)} placeholder="Find a person"
            className="min-w-0 flex-1 bg-transparent text-text outline-none placeholder:text-text-faint" />
        </label>
      </div>
      <div className="max-h-[300px] overflow-y-auto pb-1.5">
        {shown.map((person) => (
          <button key={person.id} type="button" onClick={() => { onPick(person); onClose() }}
            className={cn('flex w-full items-center gap-2.5 px-3 text-left hover:bg-hover', phone ? 'h-[52px]' : 'h-10')}>
            <Face person={person} size={24} />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-[13px] font-semibold">{person.name}</span>
              <span className="truncate text-[11px] text-text-muted">{person.role} · {person.place}</span>
            </span>
          </button>
        ))}
        {shown.length === 0 && <div className="px-3 py-3 text-[12.5px] text-text-muted">Nobody else to add.</div>}
      </div>
    </Floating>
  )
}

interface AttachHit { type: 'job' | 'contact' | 'property'; id: string; label: string; sub: string }

/** §8.2's *Add* under Attached to: one search over jobs, contacts and properties (§9.2's endpoint). */
export function AttachPicker({
  open, onClose, anchor, onPick,
}: {
  open: boolean
  onClose: () => void
  anchor: HTMLElement | null
  onPick: (hit: { type: 'job' | 'contact' | 'property'; id: string; label: string }) => void
}) {
  const phone = usePhone()
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<AttachHit[]>([])
  useEffect(() => { if (!open) { setQ(''); setHits([]) } }, [open])
  useEffect(() => {
    if (!open || !q.trim()) { setHits([]); return undefined }
    let live = true
    const timer = window.setTimeout(() => {
      activityRequest<{ jobs: AttachHit[]; contacts: AttachHit[]; properties: AttachHit[] }>(
        `/activity/attach?q=${encodeURIComponent(q.trim())}`)
        .then((answer) => { if (live) setHits([...answer.jobs, ...answer.contacts, ...answer.properties]) })
        .catch(() => { if (live) setHits([]) })
    }, 180)
    return () => { live = false; window.clearTimeout(timer) }
  }, [open, q])
  return (
    <Floating open={open} onClose={onClose} anchor={anchor} width={340} label="Attach to">
      <div className="p-2">
        <label className="flex h-9 items-center gap-2 rounded-lg border border-line px-2.5 text-[13px] text-text-faint">
          <Glyph name="search" size={14} />
          <input autoFocus value={q} onChange={(event) => setQ(event.target.value)}
            placeholder="A job, a contact or a property"
            className="min-w-0 flex-1 bg-transparent text-text outline-none placeholder:text-text-faint" />
        </label>
      </div>
      <div className="max-h-[320px] overflow-y-auto pb-1.5">
        {hits.map((hit) => (
          <button key={`${hit.type}:${hit.id}`} type="button" onClick={() => { onPick(hit); onClose() }}
            className={cn('flex w-full items-center gap-2.5 px-3 text-left hover:bg-hover', phone ? 'h-[52px]' : 'h-11')}>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-line-soft text-text-muted">
              <Glyph name={linkGlyph(hit.type)} size={14} />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-[13px] font-semibold">{hit.label}</span>
              {hit.sub && <span className="truncate text-[11px] text-text-muted">{hit.sub}</span>}
            </span>
          </button>
        ))}
      </div>
    </Floating>
  )
}
